import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Share2, Copy, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const TIER_COLOR = {
  "Red-Hot": "text-destructive",
  "Hot": "text-amber-accent",
  "Warming": "text-signal-500",
  "Cold": "text-muted-foreground",
};

export default function PublicScorePage() {
  const { company } = useParams();
  const [params] = useSearchParams();
  const embed = params.get("embed") === "1";
  const [data, setData] = useState(null);
  const [q, setQ] = useState(company || "");

  useEffect(() => {
    if (!company) return;
    api.get(`/public/score?company=${encodeURIComponent(company)}&narrative=true`).then((r) => setData(r.data));
  }, [company]);

  const copyEmbed = () => {
    const url = `${window.location.origin}/score/${encodeURIComponent(company)}?embed=1`;
    const iframe = `<iframe src="${url}" width="380" height="220" style="border:1px solid #ddd;border-radius:6px"></iframe>`;
    navigator.clipboard.writeText(iframe);
    toast.success("Embed snippet copied");
  };

  if (!company) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="px-6 lg:px-12 py-5 border-b border-border flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-sm bg-foreground text-background flex items-center justify-center font-black tracking-tighter">L</div>
            <span className="font-display font-semibold tracking-tightest">LogiGrowth<span className="text-signal-500">.</span>AI</span>
          </Link>
        </header>
        <main className="flex-1 px-6 lg:px-12 py-16 max-w-3xl">
          <div className="label-eyebrow mb-3">Public Tool</div>
          <h1 className="font-display font-light text-4xl lg:text-6xl tracking-tightest leading-[1.05]">
            Logistics Signal<br /><span className="font-black">Score Index.</span>
          </h1>
          <p className="mt-5 text-muted-foreground max-w-xl">
            Free intelligence on any logistics company's GTM readiness — fleet expansion, warehouse openings, customs activity, hiring signals and more.
          </p>
          <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) window.location.href = `/score/${encodeURIComponent(q.trim())}`; }}
            className="mt-10 max-w-lg flex gap-2" data-testid="signal-search-form">
            <Input data-testid="signal-search-input" placeholder="e.g. Maersk, FedEx, DHL..." value={q} onChange={(e) => setQ(e.target.value)} className="h-12 text-base" />
            <Button type="submit" size="lg" className="h-12 rounded-sm" data-testid="signal-search-btn">Score it<ArrowRight className="w-4 h-4 ml-1.5" /></Button>
          </form>
          <div className="mt-10 text-xs text-muted-foreground">Powered by LogiGrowth's logistics intelligence engine</div>
        </main>
      </div>
    );
  }

  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Computing score for {company}...</div>;

  const tierColor = TIER_COLOR[data.tier] || "text-muted-foreground";

  if (embed) {
    return (
      <div className="min-h-screen p-5 bg-background text-foreground" data-testid="signal-embed">
        <div className="border border-border rounded-sm p-5 bg-card">
          <div className="label-eyebrow mb-2">Logistics Signal Score</div>
          <div className="flex items-end gap-3 mb-3">
            <span className={`text-5xl font-display font-semibold ${tierColor}`}>{data.score}</span>
            <span className="text-xs text-muted-foreground mb-2">/100 · {data.tier}</span>
          </div>
          <div className="text-sm font-medium mb-1">{data.company}</div>
          <Link to={`/score/${encodeURIComponent(company)}`} target="_blank" rel="noreferrer" className="text-[0.65rem] tracking-widest uppercase text-signal-500 hover:underline inline-flex items-center gap-1 mt-2">
            View full report on LogiGrowth.AI <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="signal-report">
      <header className="px-6 lg:px-12 py-5 border-b border-border flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-sm bg-foreground text-background flex items-center justify-center font-black tracking-tighter">L</div>
          <span className="font-display font-semibold tracking-tightest">LogiGrowth<span className="text-signal-500">.</span>AI</span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/score" className="text-sm text-muted-foreground hover:text-foreground">Score another</Link>
          <Button variant="outline" size="sm" onClick={copyEmbed} data-testid="signal-embed-btn"><Share2 className="w-3.5 h-3.5 mr-1.5" />Embed</Button>
        </div>
      </header>
      <main className="px-6 lg:px-12 py-14 max-w-5xl">
        <div className="label-eyebrow">Logistics Signal Report</div>
        <h1 className="mt-2 font-display font-light text-4xl lg:text-6xl tracking-tightest">{data.company}</h1>
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 data-card">
            <div className="label-eyebrow mb-2">Overall score</div>
            <div className={`text-7xl font-display font-semibold tracking-tightest ${tierColor}`}>{data.score}</div>
            <div className="mt-1 text-xs text-muted-foreground">/ 100</div>
            <div className={`mt-4 text-sm font-semibold ${tierColor}`}>● {data.tier}</div>
            {data.narrative && <p className="mt-4 text-sm text-muted-foreground leading-relaxed border-t border-border pt-4">{data.narrative}</p>}
          </div>
          <div className="lg:col-span-2 data-card">
            <div className="label-eyebrow mb-3">Signal components</div>
            <ul className="space-y-3">
              {data.components.map((c) => (
                <li key={c.id} data-testid={`signal-component-${c.id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium">{c.label}</span>
                    <span className="font-mono text-xs text-muted-foreground">w{c.weight} · {c.value}/100</span>
                  </div>
                  <div className="h-1.5 bg-muted rounded-sm overflow-hidden">
                    <div className="h-full bg-signal-500" style={{ width: `${c.value}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-6">
          <div className="text-xs text-muted-foreground inline-flex items-center gap-2"><Activity className="w-3 h-3" />Computed {new Date(data.computed_at).toLocaleString()}</div>
          <Link to="/signup" className="text-sm font-medium inline-flex items-center gap-1.5 hover:underline">Get the full AI Revenue OS <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
      </main>
    </div>
  );
}
